import { ChevronRight, Home } from "lucide-react";
import { Link } from "react-router";

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  className?: string;
}

/**
 * Breadcrumbs Component
 * 
 * Enterprise-grade navigation breadcrumbs for ADJUDON.
 * Follows Government-Grade design standards with 0px border-radius.
 * 
 * @param items - Array of breadcrumb items with label and optional href
 * @param className - Optional additional CSS classes
 */
export function Breadcrumbs({ items, className = "" }: BreadcrumbsProps) {
  return (
    <nav
      aria-label="Breadcrumb"
      className={`flex items-center gap-2 text-sm ${className}`}
    >
      {/* Home Icon */}
      <Link
        to="/"
        className="text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors"
        aria-label="Home"
      >
        <Home className="w-4 h-4" />
      </Link>

      {items.map((item, index) => {
        const isLast = index === items.length - 1;

        return (
          <div key={index} className="flex items-center gap-2">
            {/* Separator */}
            <ChevronRight className="w-4 h-4 text-[var(--border)]" aria-hidden="true" />

            {/* Breadcrumb Item */}
            {isLast ? (
              <span
                className="font-medium text-[var(--foreground)] font-mono uppercase tracking-wider text-xs"
                aria-current="page"
              >
                {item.label}
              </span>
            ) : item.href ? (
              <Link
                to={item.href}
                className="text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors font-mono uppercase tracking-wider text-xs"
              >
                {item.label}
              </Link>
            ) : (
              <span className="text-[var(--muted-foreground)] font-mono uppercase tracking-wider text-xs">
                {item.label}
              </span>
            )}
          </div>
        );
      })}
    </nav>
  );
}
